import { autoinject, bindable, Container } from 'aurelia-framework';
import { SearchResult } from 'elements/search/search-input';
import { PubSub } from 'lib/event/PubSub';
import { Keyboard } from 'lib/helpers/keyboard';
import { AsyncErrorHandler } from 'lib/ui';
import {
  MooringArticleSearchResult,
  MooringProductionBuoyDto,
  MooringProductionBuoyEditDto,
  MooringProductionBuoySlotDto,
} from 'models/mooring';
import { MooringComponentSearchHandler, MooringService } from 'services/mooring';
import { MooringComponentSearchResult } from 'services/mooring/mooring-component-search-handler';
import { OfferViewContext } from 'views/mooring/mooring-offers/OfferViewContext';

@autoinject()
export class MooringProductionBuoysTable {
  @bindable mooringId!: number;

  protected buoys: MooringProductionBuoyDto[] = [];
  protected editRows: MooringProductionBuoyEditDto[] = [];
  protected isEditing = false;
  protected context = OfferViewContext.useContext(this.container);

  constructor(
    private container: Container,
    private mooringService: MooringService,
    private pubsub: PubSub,
    protected mooringComponentSearchHandler: MooringComponentSearchHandler
  ) {
    this.pubsub.sub('list-entity:updated', () => {
      if (!this.isEditing) void this.getBuoys();
    });
    this.pubsub.sub('entity:updated', () => {
      if (!this.isEditing) void this.getBuoys();
    });
  }

  protected bind() {
    if (!this.mooringId) {
      throw new Error(`Mooring id is missing.`);
    }
  }

  protected async attached() {
    await this.mooringComponentSearchHandler.preCache();
    await this.getBuoys();
  }

  protected async setEdit() {
    if (this.isEditing) {
      await this.getBuoys();
      this.editRows = [];
      this.isEditing = false;
      return;
    }

    this.editRows = this.buoys.map(
      (b) =>
        ({
          BuoyId: b.Id,
          AnalysisNo: b.AnalysisNo,
          PullDownKg: b.PullDownKg,
          IsLightBuoy: b.IsLightBuoy,
          BuoySlot: b.BuoySlot ?? undefined,
          MainStrapSlot: b.MainStrapSlot ?? undefined,
          BuoyStrapSlot: b.BuoyStrapSlot ?? undefined,
          AdditionalSafetySlot: b.AdditionalSafetySlot ?? undefined,
          ShackleSlot: b.ShackleSlot ?? undefined,
        }) as MooringProductionBuoyEditDto
    );
    this.isEditing = true;
  }

  protected onArticleSelected(searchResult: MooringComponentSearchResult, slot: MooringProductionBuoySlotDto) {
    if (!searchResult?.id || !slot) return;
    const article = searchResult.return;
    if (!article) return;
    slot.ArticleProductId = article.Id;
    slot.ProductName = article.ProductName;
  }

  @AsyncErrorHandler
  protected async save() {
    if (!this.editRows) return;
    await this.mooringService.updateProductionBuoys(this.mooringId, this.editRows);
    this.editRows = [];
    this.isEditing = false;
    await this.getBuoys();
  }

  @AsyncErrorHandler
  private async getBuoys() {
    const { data } = await this.mooringService.getProductionBuoys(this.mooringId);
    this.buoys = data ?? [];
  }

  protected unbind() {
    this.pubsub.unsub();
  }

  protected detached() {
    this.pubsub.unsub();
  }

  /**
   * Handles the Enter key to navigate to the next line in the table.
   * If Shift + Enter is pressed, it navigates to the previous line.
   * field is the name of the field.
   * @param event The keyboard event.
   * @param parentIndex The index of the outer table row, not the article subtable index.
   * @param field The field name to identify the property/input.
   */
  protected tryGotoNextLine(event: KeyboardEvent, parentIndex: number, field: string) {
    console.log("parentIndex", parentIndex);

    
    const isEnterKey = Keyboard.isEnterKey(event);
    const isShiftEnter = Keyboard.isShiftEnterKey(event);

    if (!isEnterKey && !isShiftEnter) {
      return;
    }

    event.preventDefault();

    let nextIndex = isShiftEnter ? parentIndex - 1 : parentIndex + 1;
    nextIndex = Math.max(0, Math.min(this.buoys.length - 1, nextIndex));

    const nextRow = document.querySelector(`[data-identifier="${field}-${nextIndex}"]`);

    if (!nextRow) {
      return;
    }

    nextRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
    (nextRow as HTMLInputElement | HTMLTextAreaElement | null)?.focus();
  }
}
